import {
  DataFileInterface,
  FileSelectorModal,
  FileSelectorModalProps,
  FileTypeEnum,
  FileUploaderModal,
  FileUploaderModalProps,
  convertFileInfoToDataFile,
  fileInputAcceptTypes,
  isAcceptTypeValid,
  t as tFileManager,
} from '@systemeio/file-manager'
import { PenIcon } from '@systemeio/ui/icons'
import { toast } from '@systemeio/ui'
import React, { FC, ReactElement, useRef, useState } from 'react'

interface AvatarUploaderProps
  extends Pick<FileUploaderModalProps, 'fileManagerApiType' | 'source'> {
  onSetPhoto: (file: DataFileInterface) => void
  children: ReactElement<any>
  width?: number
  height?: number
}

const initialSearchFilters = {
  name: '',
  source: [],
  type: [FileTypeEnum.Image, FileTypeEnum.ImageOther],
}

export const AvatarUploader: FC<AvatarUploaderProps> = ({
  onSetPhoto,
  children,
  width,
  height,
  source,
  fileManagerApiType,
}: AvatarUploaderProps) => {
  const [isOpened, setIsOpened] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  const uploaderRef = useRef(undefined) as NonNullable<
    FileSelectorModalProps['fileUploaderModalProps']
  >['uploaderRef']
  const fileSelectorRef = useRef<
    NonNullable<FileSelectorModalProps['fileSelectorRef']>['current']
  >(undefined)

  const onAddFile = (file: File) => {
    setIsOpened(true)
    setTimeout(() => {
      fileSelectorRef?.current?.setFileUploadModalOpen(true)
      uploaderRef?.current?.setWaitingUploadFiles(prev => [...prev, { file }])
    })
  }

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    const { files } = e.dataTransfer
    if (files && files.length) {
      const file = files[0]
      if (!isAcceptTypeValid(file.type, fileInputAcceptTypes.image)) {
        toast.error(tFileManager('file_manager.file_upload.file_not_supported'))
      } else {
        onAddFile(file)
      }
    }
    setIsDragging(false)
  }
  return (
    <>
      <label>
        <input
          type="file"
          className="invisible absolute h-1 w-0"
          onClick={e => {
            e.preventDefault()
            setIsOpened(true)
          }}
        />
        <div
          className={`relative cursor-pointer rounded-full ${
            isDragging && 'ring-brand-primary ring-2'
          }`}
          style={{
            width: width,
            height: height,
          }}
          onDragStart={handleDrag}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onDragOver={handleDrag}
        >
          {children}
          <div
            className={
              'bg-brand-primary absolute bottom-0 right-0 flex h-5 w-5 translate-x-1/3 items-center justify-center rounded-full p-1'
            }
          >
            <PenIcon className={'fill-background-secondary h-4 w-4'} />
          </div>
        </div>
      </label>

      {!fileManagerApiType?.isPublic && (
        <FileSelectorModal
          opened={isOpened}
          uploadingFromSource={source}
          fileSelectorRef={fileSelectorRef}
          disabledFileTypeSelect
          initialSearchFilters={initialSearchFilters}
          fileUploaderModalProps={{
            uploaderRef,
            imageCropperModalProps: {
              isRounded: true,
              imageCropperProps: {
                aspectRatio: 1,
              },
            },
            acceptTypes: fileInputAcceptTypes.image,
          }}
          onSelect={file => {
            setIsOpened(false)
            onSetPhoto(file)
          }}
          onClose={() => setIsOpened(false)}
        />
      )}

      {fileManagerApiType?.isPublic && (
        <FileUploaderModal
          opened={isOpened}
          onClose={() => setIsOpened(false)}
          source={source}
          requireCropping
          fileManagerApiType={{ isPublic: true }}
          showCroppingNote
          singleFileMode
          openCroppingModalOnAdding
          uploaderRef={uploaderRef}
          acceptTypes={fileInputAcceptTypes.image}
          actionLabel={tFileManager('file_manager.file_upload.upload_and_select_file')}
          imageCropperModalProps={{
            isRounded: true,
            imageCropperProps: {
              aspectRatio: 1,
            },
          }}
          onUploadFinish={uploadedFiles => {
            const uploadedFile = uploadedFiles[0]
            if (!uploadedFile) return
            onSetPhoto(convertFileInfoToDataFile(uploadedFile, source))
            setIsOpened(false)
          }}
        />
      )}
    </>
  )
}
